// ProgramManagement - Admin interface for creating and managing expense programs
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  MenuItem,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Unarchive as UnarchiveIcon,
  People as PeopleIcon,
  CalendarMonth as CalendarIcon,
  AttachMoney as MoneyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BulkCreateDialog from './BulkCreateDialog';
import { programsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useUser } from '../../contexts/UserContext';
import dayjs from 'dayjs';

const DEFAULT_CATEGORIES = [
  'Travel',
  'Meals',
  'Office Supplies',
  'Equipment',
  'Training',
  'Professional Development',
  'Software/Subscriptions',
  'Other'
];

const ProgramManagement: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { isAdmin } = useUser();
  const [bulkCreateDialogOpen, setBulkCreateDialogOpen] = useState(false);
  const [selectedProgramForBulk, setSelectedProgramForBulk] = useState<any>(null);
  const [programs, setPrograms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<any>(null);
  const [generalProgram, setGeneralProgram] = useState<any>(null); // ADDED: Track General program
  const [togglingGeneral, setTogglingGeneral] = useState(false); // ADDED: Toggle loading state

  // Category management state
  const [newCategory, setNewCategory] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    programName: '',
    description: '',
    instructions: '',
    allowance: '',
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().add(3, 'months').format('YYYY-MM-DD'),
    maxSubmissionsPerUser: '',
    assignmentType: 'All',
    expenseCategories: [...DEFAULT_CATEGORIES],
    isActive: true,
  });

  useEffect(() => {
    if (isAdmin) {
      fetchPrograms();
    }
  }, [isAdmin, showArchived]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const data = await programsAPI.getAllPrograms(showArchived);
      
      // ADDED: Separate General program from others
      const allPrograms = data.programs || [];
      const general = allPrograms.find((p: any) => p.isGeneral);
      const others = allPrograms.filter((p: any) => !p.isGeneral);
      
      setGeneralProgram(general || null);
      setPrograms(others);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOpen = () => {
    setFormData({
      programName: '',
      description: '',
      instructions: '',
      allowance: '',
      startDate: dayjs().format('YYYY-MM-DD'),
      endDate: dayjs().add(3, 'months').format('YYYY-MM-DD'),
      maxSubmissionsPerUser: '',
      assignmentType: 'All',
      expenseCategories: [...DEFAULT_CATEGORIES],
      isActive: true,
    });
    setNewCategory('');
    setCreateDialogOpen(true);
  };

  const handleEditOpen = async (program: any) => {
    setSelectedProgram(program);
    setFormData({
      programName: program.programName,
      description: program.description || '',
      instructions: program.instructions || '',
      allowance: program.allowance?.toString() || '',
      startDate: dayjs(program.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(program.endDate).format('YYYY-MM-DD'),
      maxSubmissionsPerUser: program.maxSubmissionsPerUser?.toString() || '',
      assignmentType: program.assignmentType || 'All',
      expenseCategories: program.expenseCategories || [...DEFAULT_CATEGORIES],
      isActive: program.isActive,
    });
    setNewCategory('');
    setEditDialogOpen(true);
  };

  const handleAddCategory = () => {
    const trimmedCategory = newCategory.trim();
    
    if (!trimmedCategory) {
      showError('Category name cannot be empty');
      return;
    }

    if (formData.expenseCategories.includes(trimmedCategory)) {
      showError('Category already exists');
      return;
    }

    setFormData({
      ...formData,
      expenseCategories: [...formData.expenseCategories, trimmedCategory]
    });
    setNewCategory('');
  };

  const handleRemoveCategory = (categoryToRemove: string) => {
    if (formData.expenseCategories.length <= 1) {
      showError('At least one category is required');
      return;
    }

    setFormData({
      ...formData,
      expenseCategories: formData.expenseCategories.filter(cat => cat !== categoryToRemove)
    });
  };

  const handleCreate = async () => {
    try {
      if (formData.expenseCategories.length === 0) {
        showError('At least one expense category is required');
        return;
      }

      const payload = {
        ...formData,
        allowance: formData.allowance ? parseFloat(formData.allowance) : null,
        maxSubmissionsPerUser: formData.maxSubmissionsPerUser ? parseInt(formData.maxSubmissionsPerUser) : null,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
      };

      await programsAPI.createProgram(payload);
      showSuccess('Program created successfully');
      setCreateDialogOpen(false);
      fetchPrograms();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to create program');
    }
  };

  const handleUpdate = async () => {
    if (!selectedProgram) return;

    try {
      if (formData.expenseCategories.length === 0) {
        showError('At least one expense category is required');
        return;
      }

      const payload = {
        ...formData,
        allowance: formData.allowance ? parseFloat(formData.allowance) : null,
        maxSubmissionsPerUser: formData.maxSubmissionsPerUser ? parseInt(formData.maxSubmissionsPerUser) : null,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
      };

      await programsAPI.updateProgram(selectedProgram.programId, payload);
      showSuccess('Program updated successfully');
      setEditDialogOpen(false);
      fetchPrograms();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to update program');
    }
  };

  const handleArchive = async (programId: string) => {
    if (!window.confirm('Archive this program? Employees will no longer be able to submit.')) {
      return;
    }

    try {
      await programsAPI.archiveProgram(programId);
      showSuccess('Program archived');
      fetchPrograms();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to archive program');
    }
  };

  const handleUnarchive = async (programId: string) => {
    try {
      await programsAPI.unarchiveProgram(programId);
      showSuccess('Program unarchived');
      fetchPrograms();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to unarchive program');
    }
  };

  const handleDelete = async (programId: string) => {
    if (!window.confirm('Delete this program? This action cannot be undone.')) {
      return;
    }

    try {
      await programsAPI.deleteProgram(programId);
      showSuccess('Program deleted');
      fetchPrograms();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to delete program');
    }
  };

  // ADDED: Toggle General Expense availability
  const handleToggleGeneralExpense = async () => {
    if (!generalProgram) return;

    const newActiveState = !generalProgram.isActive;
    const action = newActiveState ? 'enable' : 'disable';

    if (!window.confirm(`Are you sure you want to ${action} General Expense Reimbursement for all employees?`)) {
      return;
    }

    try {
      setTogglingGeneral(true);
      
      await programsAPI.updateProgram(generalProgram.programId, {
        ...generalProgram,
        isActive: newActiveState,
      });

      showSuccess(`General Expense Reimbursement ${newActiveState ? 'enabled' : 'disabled'}`);
      fetchPrograms();
    } catch (err: any) {
      showError(err.response?.data?.error || `Failed to ${action} General Expense`);
    } finally {
      setTogglingGeneral(false);
    }
  };

  if (!isAdmin) {
    return (
      <Alert severity="warning">
        You do not have permission to access this page.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Program Management</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
            }
            label="Show Archived"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateOpen}
          >
            Create Program
          </Button>
        </Box>
      </Box>

      {/* ADDED: General Expense Control Card */}
      {generalProgram && (
        <Card 
          sx={{ 
            mb: 3, 
            border: '2px solid',
            borderColor: generalProgram.isActive ? 'success.main' : 'grey.400',
            bgcolor: generalProgram.isActive ? 'success.50' : 'grey.50',
          }}
        >
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <Typography variant="h6">
                    General Expense Reimbursement
                  </Typography>
                  <Chip 
                    label="System Program" 
                    size="small" 
                    color="primary" 
                  />
                  <Chip
                    label={generalProgram.isActive ? "Enabled" : "Disabled"}
                    size="small"
                    color={generalProgram.isActive ? "success" : "default"}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {generalProgram.description || 'Allow employees to submit general expense reimbursements at any time'}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5} mt={1.5}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    Categories:
                  </Typography>
                  {generalProgram.expenseCategories?.map((cat: string) => (
                    <Chip key={cat} label={cat} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={generalProgram.isActive}
                      onChange={handleToggleGeneralExpense}
                      disabled={togglingGeneral}
                      color="success"
                    />
                  }
                  label={generalProgram.isActive ? "Enabled" : "Disabled"}
                  labelPlacement="start"
                />
                <Tooltip title="Edit Categories">
                  <IconButton
                    size="small"
                    onClick={() => handleEditOpen(generalProgram)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            {!generalProgram.isActive && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>General Expense Reimbursement is currently disabled.</strong>
                  <br />
                  • Employees cannot create new general expense submissions
                  <br />
                  • The program will not appear on the employee dashboard
                  <br />
                  • Existing submissions can still be viewed and processed
                </Typography>
              </Alert>
            )}
            {generalProgram.isActive && (
              <Typography variant="caption" color="success.dark" display="block" sx={{ mt: 1.5 }}>
                ✓ Employees can create general expense submissions at any time
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      <Typography variant="h6" gutterBottom>
        Custom Programs
      </Typography>

      <Grid container spacing={3}>
        {programs.map((program) => (
          <Grid item xs={12} md={6} key={program.programId}>
            <Card
              sx={{
                opacity: program.isArchived ? 0.7 : 1,
                border: program.isGeneral ? '2px solid' : undefined,
                borderColor: program.isGeneral ? 'primary.main' : undefined,
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                  <Box>
                    <Typography variant="h6">
                      {program.programName}
                      {program.isGeneral && (
                        <Chip
                          label="System"
                          size="small"
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {program.description}
                    </Typography>
                  </Box>
                  <Box>
                    {program.isArchived ? (
                      <Chip label="Archived" size="small" color="default" />
                    ) : program.isCurrentlyActive ? (
                      <Chip label="Active" size="small" color="success" />
                    ) : (
                      <Chip label="Inactive" size="small" color="default" />
                    )}
                  </Box>
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {dayjs(program.startDate).format('MMM D, YYYY')} - {dayjs(program.endDate).format('MMM D, YYYY')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <MoneyIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {program.allowance ? `$${program.allowance.toFixed(2)} limit` : 'No limit'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <PeopleIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {program.totalSubmissions} submissions
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Max: {program.maxSubmissionsPerUser || 'Unlimited'}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Show Expense Categories */}
                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    Expense Categories:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {program.expenseCategories?.map((cat: string) => (
                      <Chip key={cat} label={cat} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>

                {!program.isGeneral && (
                  <Box display="flex" gap={1} justifyContent="flex-end">
  {!program.isArchived && (
    <>
      <Tooltip title="Create Bulk Submissions">
        <IconButton
          size="small"
          color="primary"
          onClick={() => {
            setSelectedProgramForBulk(program);
            setBulkCreateDialogOpen(true);
          }}
        >
          <PeopleIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit">
        <IconButton
          size="small"
          onClick={() => handleEditOpen(program)}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Archive">
        <IconButton
          size="small"
          onClick={() => handleArchive(program.programId)}
        >
          <ArchiveIcon />
        </IconButton>
      </Tooltip>
    </>
  )}
  {program.isArchived && (
    <Tooltip title="Unarchive">
      <IconButton
        size="small"
        onClick={() => handleUnarchive(program.programId)}
      >
        <UnarchiveIcon />
      </IconButton>
    </Tooltip>
  )}
  {program.totalSubmissions === 0 && (
    <Tooltip title="Delete">
      <IconButton
        size="small"
        color="error"
        onClick={() => handleDelete(program.programId)}
      >
        <DeleteIcon />
      </IconButton>
    </Tooltip>
  )}
</Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen || editDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {createDialogOpen ? 'Create New Program' : selectedProgram?.isGeneral ? 'Edit General Expense Categories' : 'Edit Program'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Show info alert for General program editing */}
            {selectedProgram?.isGeneral && (
              <Grid item xs={12}>
                <Alert severity="info">
                  You can only edit the expense categories for the General Expense program. 
                  Other settings are system-managed.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Program Name"
                value={formData.programName}
                onChange={(e) => setFormData({ ...formData, programName: e.target.value })}
                required
                disabled={selectedProgram?.isGeneral}  
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                disabled={selectedProgram?.isGeneral}  
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
                disabled={selectedProgram?.isGeneral}  
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
                disabled={selectedProgram?.isGeneral}  
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Allowance per User ($)"
                value={formData.allowance}
                onChange={(e) => setFormData({ ...formData, allowance: e.target.value })}
                helperText="Leave empty for no limit"
                disabled={selectedProgram?.isGeneral}  
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Submissions per User"
                value={formData.maxSubmissionsPerUser}
                onChange={(e) => setFormData({ ...formData, maxSubmissionsPerUser: e.target.value })}
                helperText="Leave empty for unlimited"
                disabled={selectedProgram?.isGeneral}  
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Assignment Type"
                value={formData.assignmentType}
                onChange={(e) => setFormData({ ...formData, assignmentType: e.target.value })}
                disabled={selectedProgram?.isGeneral}  
              >
                <MenuItem value="All">All Users</MenuItem>
                <MenuItem value="Groups">Specific Groups</MenuItem>
                <MenuItem value="Individuals">Specific Users</MenuItem>
              </TextField>
            </Grid>

            {/* EXPENSE CATEGORIES SECTION */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Expense Categories *
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  {selectedProgram?.isGeneral 
                    ? 'These categories are available for all general expense submissions'
                    : 'Define which expense categories employees can use for this program'
                  }
                </Typography>

                {/* Add Category Input */}
                <Box display="flex" gap={1} mb={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Add Category"
                    placeholder="e.g., Conference, Parking"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCategory();
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddCategory}
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                </Box>

                {/* Category Chips */}
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {formData.expenseCategories.map((category) => (
                    <Chip
                      key={category}
                      label={category}
                      onDelete={() => handleRemoveCategory(category)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>

                {formData.expenseCategories.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    At least one category is required
                  </Alert>
                )}
              </Paper>
            </Grid>

            {/* Only show Active toggle for custom programs */}
            {!selectedProgram?.isGeneral && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setEditDialogOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={createDialogOpen ? handleCreate : handleUpdate}
            disabled={
              !formData.programName || 
              !formData.startDate || 
              !formData.endDate ||
              formData.expenseCategories.length === 0
            }
          >
            {createDialogOpen ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    {/* Bulk Creation Dialog */}
      {selectedProgramForBulk && (
        <BulkCreateDialog
          open={bulkCreateDialogOpen}
          onClose={() => {
            setBulkCreateDialogOpen(false);
            setSelectedProgramForBulk(null);
          }}
          program={selectedProgramForBulk}
          onSuccess={() => {
            fetchPrograms(); // Refresh program list
          }}
        />
      )}
    </Box>
  );
};

export default ProgramManagement;